"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  Alert,
  AlertIcon,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Card,
  CardBody,
  useToast,
  Spinner,
  Center,
  Image,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config";

export default function Account() {
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userData, setUserData] = useState({
    email: "",
    username: "",
    fullName: "",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();

  useEffect(() => {
    loadUserData();
  }, [user, authLoading]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select(`username, full_name`)
        .eq("id", user.id)
        .single();

      if (error) {
        throw error;
      }

      setUserData({
        email: user.email || "",
        username: data?.username || "",
        fullName: data?.full_name || "",
      });
    } catch (error: any) {
      console.error("Error loading user data:", error.message);
      toast({
        title: "Error loading user data",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setUpdateLoading(true);
    try {
      if (!user) {
        throw new Error("User not logged in.");
      }

      const updates = {
        id: user.id,
        username: userData.username,
        full_name: userData.fullName,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("profiles").upsert(updates);

      if (error) {
        throw error;
      }

      toast({
        title: "Profile updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error updating profile:", error.message);
      toast({
        title: "Error updating profile",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      if (!user) {
        throw new Error("User not logged in.");
      }

      // First, cancel any active Stripe subscription
      const response = await fetch(`${API_BASE_URL}/delete-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            (
              await supabase.auth.getSession()
            ).data.session?.access_token
          }`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete account");
      }

      // Sign out the user
      await supabase.auth.signOut();

      toast({
        title: "Account deleted successfully",
        description: "Your account and all associated data have been deleted.",
        status: "success",
        duration: 5000,
      });

      // Redirect to home or login page
      window.location.href = "/";
    } catch (error: any) {
      console.error("Error deleting account:", error.message);
      toast({
        title: "Error deleting account",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setDeleteLoading(false);
      onClose();
    }
  };

  if (authLoading || loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  if (!user) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody textAlign="center">
            <Text>Please log in to view your account.</Text>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>
            <Button
              as={Link}
              to="/dashboard"
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              colorScheme="primary"
            >
              Back to Dashboard
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="2xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading size="lg" mb={2} color="gray.700">
              Account Settings
            </Heading>
            <Text color="gray.600">
              Manage your profile information and account preferences
            </Text>
          </Box>

          {/* Profile Information */}
          <Card>
            <CardBody p={8}>
              <VStack spacing={6}>
                <Heading size="md" color="gray.700" alignSelf="flex-start">
                  Profile Information
                </Heading>

                <VStack spacing={4} w="100%">
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={userData.email}
                      isDisabled
                      bg="gray.100"
                      cursor="not-allowed"
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Email cannot be changed
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      type="text"
                      value={userData.fullName}
                      onChange={(e) =>
                        setUserData({ ...userData, fullName: e.target.value })
                      }
                      bg="gray.50"
                      placeholder="Enter your full name"
                    />
                  </FormControl>

                  <HStack spacing={3} w="100%" justify="flex-end">
                    <Button
                      variant="ghost"
                      onClick={loadUserData}
                      isDisabled={updateLoading}
                      colorScheme="gray"
                      _hover={{ bg: "gray.100" }}
                      color={"gray.600"}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="primary"
                      onClick={handleSave}
                      isLoading={updateLoading}
                      loadingText="Saving..."
                    >
                      Save Changes
                    </Button>
                  </HStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Danger Zone */}
          <Card borderColor="red.200" borderWidth="2px">
            <CardBody p={8}>
              <VStack spacing={6}>
                <Heading size="md" color="red.600" alignSelf="flex-start">
                  Danger Zone
                </Heading>

                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Delete Account</Text>
                    <AlertDescription>
                      Permanently delete your account and all associated data.
                      This action cannot be undone. Any active subscriptions
                      will be cancelled.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={onOpen}
                  alignSelf="flex-start"
                >
                  Delete Account
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>

        {/* Delete Account Confirmation Modal */}
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent bg="white" borderRadius="md">
            <ModalHeader color="red.600">Confirm Account Deletion</ModalHeader>
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  Are you absolutely sure you want to delete your account?
                </Text>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertDescription>
                      This action will:
                      <br />• Permanently delete all your data
                      <br />• Cancel any active subscriptions
                      <br />• Remove access to all documents
                      <br />• Cannot be undone
                    </AlertDescription>
                  </Box>
                </Alert>
                <Text fontWeight="bold" color="red.600">
                  This action is irreversible!
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={deleteLoading}
                colorScheme="gray"
                color={"gray.600"}
                _hover={{ bg: "gray.100" }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteAccount}
                isLoading={deleteLoading}
                loadingText="Deleting..."
              >
                Delete Account
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
}
